/**
 * @description 季报头部组件 - 2026版本
 * @date 2025-09-26
 * @author lv.chen
 */
import React from 'react';
import classnames from 'classnames';
import Image from '@user-center/components/Image';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import { formatDateStr } from '@home/utils/date';
import { getGameDataGenes, getRoleConfig, getGameDataAllGenreMap } from '../../../utils';
import { GAME_DATA_GENE_GROUPS } from '../../../constants';

import IMG_LOGOS from '@user-center/assets/images/quarter-report/logos.png';

interface IQuarterReportHeaderProps extends IBaseProps {
  // 可以根据需要添加其他props
  year?: number;
  isEN: boolean;
  reportDate?: string;
  report?: IQuarterReportDetails | null;
}

/**
 * 季报头部组件
 */
const QuarterReportHeader: React.FC<IQuarterReportHeaderProps> = (props) => {
  const { style, className, year, isEN, reportDate, report } = props;

  const reportStyle = report?.style || '';
  const reportRoleConfig = getRoleConfig(reportStyle, year);
  const reportRoleImage = reportRoleConfig?.roleImage || '';
  const reportGenes = getGameDataGenes(report);
  const reportGeneMap = getGameDataAllGenreMap(report);

  const { formatMessage } = useIntlExtend();

  return (
    <div style={style} className={classnames('quarter-header', className)}>
      <div className="report-roles-show">
        {/* 素材图 */}
        <Image className="report-roles-show-img" src={reportRoleImage} />
        {/* 蓝色阴影 */}
        <div style={{ maskImage: `url(${reportRoleImage}` }} className="report-roles-show-shadow"></div>
      </div>
      <div className="quarter-header-logos">
        <Image src={IMG_LOGOS} />
      </div>
      <div className="quarter-header-date">Q{formatDateStr(reportDate, 'Q YYYY')}</div>
      <div className={classnames('quarter-header-slogan', { '--eng': isEN })}>
        {formatMessage({ id: 'dbu_quarter_report_text11' })}
      </div>
      <div className="quarter-personal">
        <div className="quarter-personal-tips">
          <div>
            Hi <span className="--blue">{report?.open_id}</span>
          </div>
          <div>
            {formatMessage({ id: 'dbu_quarter_report_text1' })}
          </div>
        </div>
        <div className="quarter-personal-role">
          <div className="quarter-personal-role-info">{formatMessage({ id: reportRoleConfig?.roleName })}</div>
          <div
            className="dbim-quarter-report-personal__info-proportion"
            dangerouslySetInnerHTML={{
              __html: formatMessage({ id: 'dbu_quarter_report_text2' }, { number: report?.proportion ?? 0 }) as string,
            }}
          ></div>

          <div
            className={classnames('quarter-personal-role-bubble', {
              '--eng': isEN,
            })}
          >
            {/* @ts-ignore */}
            {!isEN && (
              <span
                dangerouslySetInnerHTML={{
                  __html: formatMessage({ id: 'dbu_quarter_report_text3' }) as string,
                }}
              ></span>
            )}
          </div>
          <div className="quarter-personal-role-title">
            <div className="quarter-personal-role-text">{reportStyle}</div>
            <div className="quarter-personal-role-shadow">{reportStyle}</div>
            <div className="quarter-personal-role-shadow2">{reportStyle}</div>
            <div className="quarter-personal-role-shadow3">{reportStyle}</div>
          </div>
          <div className="quarter-personal-role-desc">{formatMessage({ id: reportRoleConfig?.roleExclusiveCopy })}</div>
        </div>
        <div className="quarter-personal-tab">
          {reportGenes?.map((reportGene, reportGeneIndex) => {
            const { code, name } = reportGene || {};
            return (
              <div
                key={code}
                className={classnames('quarter-personal-tab-item', {
                  '--active': reportGeneIndex === 0,
                })}
              >
                {formatMessage({ id: name })}
              </div>
            );
          })}
        </div>
        <div className="quarter-personal-tab-conent">
          <div className="skill-bar">
            {GAME_DATA_GENE_GROUPS.map((reportGenes, reportGeneGroupIndex) => {
              const [leftReportGeneCode, rightReportGeneCode] = reportGenes || [];
              const leftReportGene = reportGeneMap?.[leftReportGeneCode] || {};
              const rightReportGene = reportGeneMap?.[rightReportGeneCode] || {};
              const leftReportGeneScore = leftReportGene?.score as number;
              const rightReportGeneScore = rightReportGene?.score as number;
              const maxReportGeneScore = Math.max(leftReportGeneScore, rightReportGeneScore) || 0;
              const needReverse = rightReportGeneScore > leftReportGeneScore;

              return (
                <div
                  key={reportGeneGroupIndex}
                  className={classnames('skill-bar-item', {
                    'skill-bar-item --active': reportGeneGroupIndex === 0,
                  })}
                >
                  <div className="skill-bar-item-label --left">
                    {formatMessage({ id: leftReportGene?.name })}({leftReportGeneCode})
                  </div>
                  <div
                    className={classnames('skill-bar-progress', {
                      'is-reverse': needReverse,
                    })}
                  >
                    <div style={{ width: `${maxReportGeneScore}%` }} className="skill-bar-progress-fill">
                      <div className="skill-bar-progress-value">{maxReportGeneScore}</div>
                    </div>
                  </div>
                  <div className="skill-bar-item-label --right">
                    ({rightReportGeneCode}){formatMessage({ id: rightReportGene?.name })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuarterReportHeader;
